import {useMutation, useQuery, useQueryClient} from 'react-query'
import {
  adoptSharpet,
  changeSharpetSpecies,
  getMySharpet,
  getProfileSharpet,
  getProfileSharpetMessages,
  getSharpetMessages,
  giveProfileSharpetTreat,
  giveSharpetTreat,
  renameSharpet,
  sendProfileSharpetMessage,
  sendSharpetMessage,
} from 'api'
import {useInvalidateCoinBalance} from 'hooks/useCoinBalance'
import {getItemFromStorage} from 'utils/storage'

/**
 * The signed-in member's Sharpet, as one shared react-query entry: the
 * corner dock and the Sharpennies page card read the same cache, so they
 * cannot disagree about whether there is a pet or how hungry it is.
 *
 * Every Sharpet endpoint answers with the same {sharpet, treatCost,
 * chatEnergyCost, speciesChangeCost} shape, so each mutation writes its response straight
 * into the cache instead of refetching. The meters drift slowly on the
 * server (a few points an hour); refetching on window focus is enough to
 * keep the pet honest.
 */
export const SHARPET_QUERY_KEY = 'sharpet'
export const SHARPET_MESSAGES_QUERY_KEY = 'sharpetMessages'

export const useSharpet = () => {
  const user = getItemFromStorage({name: 'user'})
  return useQuery(SHARPET_QUERY_KEY, getMySharpet, {
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  })
}

const useSharpetMutation = (mutationFn, onSuccess) => {
  const queryClient = useQueryClient()
  return useMutation(mutationFn, {
    onSuccess: (data) => {
      queryClient.setQueryData(SHARPET_QUERY_KEY, data)
      onSuccess?.(data)
    },
  })
}

export const useAdoptSharpet = () => useSharpetMutation(adoptSharpet)

export const useRenameSharpet = () => useSharpetMutation(renameSharpet)

// A treat spends a coin, so every balance on screen has to catch up.
export const useGiveSharpetTreat = () => {
  const invalidateCoinBalance = useInvalidateCoinBalance()
  return useSharpetMutation(giveSharpetTreat, invalidateCoinBalance)
}

// So does turning the pet into another species.
export const useChangeSharpetSpecies = () => {
  const invalidateCoinBalance = useInvalidateCoinBalance()
  return useSharpetMutation(changeSharpetSpecies, invalidateCoinBalance)
}

// Only fetched while the chat is on screen.
export const useSharpetMessages = ({enabled}) =>
  useQuery(SHARPET_MESSAGES_QUERY_KEY, getSharpetMessages, {
    enabled,
    staleTime: 5 * 60 * 1000,
  })

// The reply arrives in the same response as the pet's new state: the pet
// goes into the pet cache, the exchange onto the end of the thread.
export const useSendSharpetMessage = () => {
  const queryClient = useQueryClient()
  const invalidateCoinBalance = useInvalidateCoinBalance()
  return useMutation(sendSharpetMessage, {
    onSuccess: ({messages, balance, ...pet}) => {
      // Only a priced chat returns a balance.
      if (balance !== undefined) invalidateCoinBalance()
      queryClient.setQueryData(SHARPET_QUERY_KEY, pet)
      queryClient.setQueryData(SHARPET_MESSAGES_QUERY_KEY, (current) => ({
        messages: [...(current?.messages || []), ...messages],
      }))
    },
  })
}

// Someone else's pet, seen from their profile. Its own cache entries,
// keyed by the owner, so nothing here can overwrite the viewer's own pet.
export const PROFILE_SHARPET_QUERY_KEY = 'profileSharpet'
export const PROFILE_SHARPET_MESSAGES_QUERY_KEY = 'profileSharpetMessages'

export const useProfileSharpet = (userId, {enabled = true} = {}) =>
  useQuery(
    [PROFILE_SHARPET_QUERY_KEY, userId],
    () => getProfileSharpet(userId),
    {
      enabled: enabled && !!userId,
      staleTime: 60 * 1000,
    }
  )

export const useProfileSharpetMessages = (userId, {enabled}) =>
  useQuery(
    [PROFILE_SHARPET_MESSAGES_QUERY_KEY, userId],
    () => getProfileSharpetMessages(userId),
    {enabled: enabled && !!userId, staleTime: 5 * 60 * 1000}
  )

export const useSendProfileSharpetMessage = (userId) => {
  const queryClient = useQueryClient()
  const invalidateCoinBalance = useInvalidateCoinBalance()
  return useMutation(
    (content) => sendProfileSharpetMessage({userId, content}),
    {
      onSuccess: ({messages, balance, ...pet}) => {
        if (balance !== undefined) invalidateCoinBalance()
        queryClient.setQueryData([PROFILE_SHARPET_QUERY_KEY, userId], pet)
        queryClient.setQueryData(
          [PROFILE_SHARPET_MESSAGES_QUERY_KEY, userId],
          (current) => ({messages: [...(current?.messages || []), ...messages]})
        )
      },
    }
  )
}

export const useGiveProfileSharpetTreat = (userId) => {
  const queryClient = useQueryClient()
  const invalidateCoinBalance = useInvalidateCoinBalance()
  return useMutation(() => giveProfileSharpetTreat(userId), {
    onSuccess: ({balance, ...pet}) => {
      invalidateCoinBalance()
      queryClient.setQueryData([PROFILE_SHARPET_QUERY_KEY, userId], pet)
    },
  })
}

// When a sleeping pet wakes on its own, in words: "3:15 PM" today,
// "tomorrow at 3:15 PM" otherwise. Empty when the API sent no time.
export const sharpetWakeTime = (wakesAt) => {
  if (!wakesAt) return ''
  const at = new Date(wakesAt)
  const time = at.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})
  return at.toDateString() === new Date().toDateString()
    ? time
    : `tomorrow at ${time}`
}

// The API's own message when there is one ("Biscuit is full..."), so the
// server stays the one place that words its refusals.
export const sharpetErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

export default useSharpet
